import Image from '@base/Image';
import { forwardRef } from 'react';
import styled from '@emotion/styled';
import pushRoute from '@utils/pushRoute';
import Paper from 'aquilla/core/atoms/Paper';
import { emptyObject } from '@constants/empty';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import { convertMinsToHrsMins, getTime } from '@utils/dates';
import useHandleSaveItem from './useHandleSaveItem';
import { CARD_STATE, CARD_TYPE, CARD_WIDTH, STORE_FLAG } from './constants';
import { getCardState, getMinsDiff, useIsFromStore } from './utils';

const Wrapper = styled(Paper)`
  display: flex;
  position: relative;
  width: ${CARD_WIDTH};
  padding: ${Spacings.SPACING_6B};
  min-height: ${Spacings.SPACING_22B};
  margin-bottom: ${Spacings.SPACING_5B};
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};

  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_4B};
    min-height: ${Spacings.SPACING_18B};
  }
  cursor: pointer;
`;

const InfoWrapper = styled.div`
  width: 100%;
  margin-left: ${Spacings.SPACING_4B};
  margin-right: ${Spacings.SPACING_8B};
`;

const TestInfo = styled(Typography)`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_2};
  color: var(--color-text-secondary);
`;

const LivePill = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${Spacings.SPACING_6};
`;

const SaveWrapper = styled.div`
  position: absolute;
  top: 25px;
  right: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: ${Spacings.SPACING_10B};
`;

const testIcon = 'store/test-series/test-card-icon.svg';
const liveTestIcon = 'store/test-series/test-card-live-icon.svg';

const TestCard = forwardRef(({ testInfo, className, showSaveIcon }, ref) => {
  const fromStore = useIsFromStore();

  const {
    isLive,
    goalUid,
    isSaved: initialSaveState,
    value: { duration, startTime, title, slug = '-', uid } = emptyObject
  } = testInfo ?? emptyObject;

  const { isSaved, handleSaveClick } = useHandleSaveItem({
    uid,
    title,
    goalUid,
    initialSaveState,
    type: CARD_TYPE.QUIZ
  });

  const state = getCardState(isLive, startTime);

  const getInfoString = () => {
    if (state === CARD_STATE.LIVE) {
      return `${getMinsDiff(startTime)} / ${duration} mins remaining`;
    }
    if (state === CARD_STATE.UPCOMING) {
      return `${getTime(startTime)} • ${convertMinsToHrsMins(
        duration,
        false,
        true
      )}`;
    }
    return convertMinsToHrsMins(duration, false, true);
  };

  const handleCardClick = () => {
    pushRoute(
      `/quiz/${slug}/${uid}${fromStore ? `?source=${STORE_FLAG}` : ''}`
    );
  };

  const onSaveClick = (e) => {
    e.stopPropagation();
    handleSaveClick();
  };

  return (
    <Wrapper className={className} ref={ref} onClick={handleCardClick}>
      <IconWrapper>
        <Image
          src={state === CARD_STATE.UPCOMING ? testIcon : liveTestIcon}
          width={40}
          height={40}
          alt="test"
        />
      </IconWrapper>
      <InfoWrapper>
        <div>
          <Typography variant="h6">{title}</Typography>
          <TestInfo variant="p3">
            {state === CARD_STATE.LIVE ? (
              <LivePill>
                <Image
                  src="store/test-series/live-pill.svg"
                  width={40}
                  height={20}
                  alt="live"
                />
              </LivePill>
            ) : null}
            {getInfoString()}
          </TestInfo>
        </div>
      </InfoWrapper>
      {showSaveIcon ? (
        <SaveWrapper>
          <Image
            src={
              isSaved
                ? 'store/test-series/icon-unsave.svg'
                : 'store/test-series/icon-save.svg'
            }
            width={12}
            height={16}
            alt="save"
            onClick={onSaveClick}
          />
        </SaveWrapper>
      ) : null}
    </Wrapper>
  );
});

TestCard.displayName = 'TestCard';

export default TestCard;
